// Client Onboarding Wizard Types

export interface OnboardingProgress {
  id: string;
  client_id: string;
  current_step: number;
  step1_complete: boolean;
  step2_complete: boolean;
  step3_complete: boolean;
  step4_complete: boolean;
  step5_complete: boolean;
  step6_complete: boolean;
  // Step 1 data
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  go_live_target_date: string | null;
  // Step 2 data
  selected_payers: SelectedPayer[];
  // Step 3 data
  epic_environment: 'none' | 'sandbox' | 'production';
  epic_smart_client_id: string | null;
  epic_pushback_enabled: boolean;
  epic_pushback_destinations: string[];
  epic_mapping_validated: boolean;
  // Step 5 data
  sla_defaults_configured: boolean;
  // Metadata
  created_at: string;
  updated_at: string;
  created_by: string | null;
  last_updated_by: string | null;
}

export interface SelectedPayer {
  payer_id: string;
  payer_name: string;
  plans: SelectedPlan[];
  submission_lane_priority: string[];
}

export interface SelectedPlan {
  plan_id: string;
  plan_name: string;
  plan_type: string;
  selected_ruleset_template: string | null;
  custom_sla_hours?: number;
  custom_follow_up_cadence_hours?: number;
}

export interface GoLiveSnapshot {
  id: string;
  client_id: string;
  snapshot_version: number;
  activated_at: string;
  activated_by: string;
  client_profile: ClientProfileSnapshot;
  payer_configuration: PayerConfigSnapshot;
  epic_configuration: EpicConfigSnapshot;
  ruleset_configuration: RulesetConfigSnapshot;
  sla_configuration: SLAConfigSnapshot;
  notes: string | null;
  created_at: string;
}

export interface ClientProfileSnapshot {
  client_name: string;
  client_type: string;
  primary_state: string | null;
  primary_contact_name: string;
  primary_contact_email: string;
  go_live_target_date: string;
}

export interface PayerConfigSnapshot {
  payers: Array<{
    payer_id: string;
    payer_name: string;
    plans: Array<{
      plan_id: string;
      plan_name: string;
      plan_type: string;
    }>;
    submission_lane_priority: string[];
  }>;
}

export interface EpicConfigSnapshot {
  environment: string;
  smart_client_id: string | null;
  pushback_enabled: boolean;
  pushback_destinations: string[];
  mapping_validated: boolean;
}

export interface RulesetConfigSnapshot {
  rulesets: Array<{
    payer_id: string;
    plan_id: string;
    ruleset_name: string;
    source_template: string;
  }>;
}

export interface SLAConfigSnapshot {
  default_sla_hours: number;
  urgent_sla_hours: number;
  follow_up_cadence_hours: number;
  escalation_threshold_days: number;
  payer_overrides: Array<{
    payer_id: string;
    sla_hours: number;
    follow_up_cadence_hours: number;
  }>;
}

export interface WizardStepProps {
  clientId: string;
  progress: OnboardingProgress;
  onComplete: () => void;
  onBack?: () => void;
}

// Available payers for onboarding
export const AVAILABLE_PAYERS = [
  { id: 'ohio_bwc', name: 'Ohio BWC', type: 'workers_comp' },
  { id: 'anthem_bcbs', name: 'Anthem/BCBS', type: 'commercial' },
  { id: 'united', name: 'UnitedHealthcare', type: 'commercial' },
  { id: 'aetna', name: 'Aetna', type: 'commercial' },
  { id: 'cigna', name: 'Cigna', type: 'commercial' },
  { id: 'humana', name: 'Humana', type: 'commercial' },
] as const;

// Available plan types per payer
export const PLAN_TYPES: Record<string, string[]> = {
  ohio_bwc: ['Workers Comp', 'MCO'],
  anthem_bcbs: ['PPO', 'HMO', 'EPO', 'POS'],
  united: ['PPO', 'HMO', 'EPO', 'POS'],
  aetna: ['PPO', 'HMO', 'EPO', 'POS'],
  cigna: ['PPO', 'HMO', 'EPO', 'Open Access'],
  humana: ['PPO', 'HMO', 'Medicare Advantage'],
};

// Available ruleset templates
export const RULESET_TEMPLATES = [
  { id: 'BWC_OH_BASE_2026', name: 'Ohio BWC 2026 Base', type: 'workers_comp' },
  { id: 'COMMERCIAL_BASE_TEMPLATE_V1', name: 'Commercial Base Template V1', type: 'commercial' },
] as const;

// Submission lane options
export const SUBMISSION_LANES = [
  { id: 'fhir_pas', label: 'FHIR PAS (Automated)' },
  { id: 'portal', label: 'Payer Portal' },
  { id: 'fax', label: 'Fax' },
  { id: 'phone', label: 'Phone' },
] as const;

// Epic pushback destinations
export const EPIC_PUSHBACK_DESTINATIONS = [
  { id: 'inbasket', label: 'InBasket' },
  { id: 'task', label: 'Task' },
  { id: 'chart_note', label: 'Chart Note' },
] as const;
